import { skyness } from './nightGrade'
import type { Point } from '../types/document'
import { clamp } from './geometry'

/**
 * Classical edge detection, no AI and no server.
 *
 * Tier 1 (snapToEdge): every tap magnetizes to the real roof edge.
 * Tier 2 (traceRun):   mark a short piece and it follows that same edge
 *                      across the photo until the roof actually turns.
 *
 * Both run in a few milliseconds in the browser and work offline in a driveway.
 * Tiers 3 and 4 (detect every region, then plain language commands) sit on top
 * of this later and emit exactly the same points, so nothing downstream changes.
 */

export interface EdgeMap {
  w: number
  h: number
  /** Gradient magnitude, normalized 0..1. */
  mag: Float32Array
  /**
   * Where the sky is, at the same resolution.
   *
   * A roofline run is allowed to grow along the edge it was drawn on, and
   * without this it grows straight off the end of the roof and hangs bulbs in
   * the sky above the gable peak - which is what the export showed. The edge
   * at a roof peak does not stop being an edge just because the roof stopped:
   * the tile silhouette against the sky is one of the strongest gradients in
   * the whole photograph, so it followed it happily into mid air.
   *
   * Nothing is ever installed in the sky. This is how the extension knows.
   */
  sky: Uint8Array
}

export function buildEdgeMap(source: HTMLImageElement, maxW = 1400): EdgeMap {
  const scale = Math.min(1, maxW / source.naturalWidth)
  const w = Math.max(1, Math.round(source.naturalWidth * scale))
  const h = Math.max(1, Math.round(source.naturalHeight * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(source, 0, 0, w, h)
  const { data } = ctx.getImageData(0, 0, w, h)

  // Luminance, and the sky mask alongside it: one pass, same pixels.
  const gray = new Float32Array(w * h)
  const sky = new Uint8Array(w * h)
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255
    sky[p] = skyness(data[i], data[i + 1], data[i + 2], Math.floor(p / w) / h) > 0.5 ? 1 : 0
  }

  // Sobel
  const mag = new Float32Array(w * h)
  let peak = 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      const tl = gray[i - w - 1]
      const tc = gray[i - w]
      const tr = gray[i - w + 1]
      const ml = gray[i - 1]
      const mr = gray[i + 1]
      const bl = gray[i + w - 1]
      const bc = gray[i + w]
      const br = gray[i + w + 1]

      const gx = tl + 2 * ml + bl - (tr + 2 * mr + br)
      const gy = tl + 2 * tc + tr - (bl + 2 * bc + br)
      const m = Math.hypot(gx, gy)
      mag[i] = m
      if (m > peak) peak = m
    }
  }
  if (peak > 0) for (let i = 0; i < mag.length; i++) mag[i] /= peak

  return { w, h, mag, sky }
}

const sampleMag = (map: EdgeMap, x: number, y: number): number => {
  const xi = Math.round(x)
  const yi = Math.round(y)
  if (xi < 0 || yi < 0 || xi >= map.w || yi >= map.h) return 0
  return map.mag[yi * map.w + xi]
}

/**
 * Tier 1. Pull a tap onto the strongest nearby edge.
 * radiusFrac is a fraction of the photo width, so it feels the same on any image.
 */
/**
 * Nudge a tapped point onto the architectural edge it was aiming at.
 *
 * The radius used to be 0.012 of the photo width - 29 pixels on a 2400px shot
 * - with a falloff so gentle that a strong edge at the far end of that radius
 * beat a weaker one directly under the finger. On a barrel tile roof there is
 * always a stronger line a course or two below the one you meant, so runs
 * drifted downhill from where they were put.
 *
 * Half the reach, and the penalty for distance now grows as the square, so
 * this corrects a shaky tap rather than overruling a steady one.
 */
export function snapToEdge(map: EdgeMap, p: Point, radiusFrac = 0.006): Point {
  const px = p.x * map.w
  const py = p.y * map.h
  const r = Math.max(3, Math.round(radiusFrac * map.w))

  let bestScore = 0
  let bx = px
  let by = py

  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      const dist = Math.hypot(dx, dy)
      if (dist > r) continue
      const m = sampleMag(map, px + dx, py + dy)
      if (m < 0.08) continue
      // Prefer strong edges, but do not drag the point far from the tap.
      const near = 1 - dist / r
      const score = m * near * near
      if (score > bestScore) {
        bestScore = score
        bx = px + dx
        by = py + dy
      }
    }
  }
  if (bestScore === 0) return p
  return { x: bx / map.w, y: by / map.h }
}

export interface TraceOptions {
  /** Stop once the run has turned more than this many degrees in total. */
  maxTurnDeg?: number
  /** Give up when the edge under the walker fades below this. */
  minStrength?: number
  /** Cap the run at this fraction of the photo diagonal. */
  maxLengthFrac?: number
}

/**
 * Tier 2. Given a short marked segment, keep following that edge until it
 * fades out or the roof turns a corner. Returns the points to append,
 * simplified into a handful of anchors the user can still drag.
 */
export function traceRun(
  map: EdgeMap,
  from: Point,
  to: Point,
  opts: TraceOptions = {},
): Point[] {
  const maxTurn = ((opts.maxTurnDeg ?? 28) * Math.PI) / 180
  const minStrength = opts.minStrength ?? 0.07
  const maxLen = (opts.maxLengthFrac ?? 1.2) * Math.hypot(map.w, map.h)

  let x = to.x * map.w
  let y = to.y * map.h
  let dx = to.x * map.w - from.x * map.w
  let dy = to.y * map.h - from.y * map.h
  const seed = Math.hypot(dx, dy)
  if (seed < 2) return []
  dx /= seed
  dy /= seed

  const step = Math.max(2, map.w * 0.004)
  const searchR = Math.max(2, Math.round(map.w * 0.006))
  const raw: Point[] = []
  let traveled = 0
  let turned = 0
  let misses = 0

  for (let iter = 0; iter < 1200 && traveled < maxLen; iter++) {
    // Step forward along the current direction
    const nx = x + dx * step
    const ny = y + dy * step

    // Search perpendicular for the true edge position
    const px = -dy
    const py = dx
    let best = 0
    let bestOff = 0
    for (let o = -searchR; o <= searchR; o++) {
      const m = sampleMag(map, nx + px * o, ny + py * o)
      const score = m * (1 - (Math.abs(o) / (searchR + 1)) * 0.4)
      if (score > best) {
        best = score
        bestOff = o
      }
    }

    if (best < minStrength) {
      misses++
      // Tolerate a short gap (a downspout, a vent) before giving up
      if (misses > 4) break
    } else {
      misses = 0
    }

    const cx = nx + px * bestOff
    const cy = ny + py * bestOff

    // New direction, smoothed so noise cannot whip the walker around
    let ndx = cx - x
    let ndy = cy - y
    const nlen = Math.hypot(ndx, ndy) || 1
    ndx /= nlen
    ndy /= nlen

    const turn = Math.acos(clamp(ndx * dx + ndy * dy, -1, 1))
    turned += turn
    // A real corner: stop and let the user start the next run
    if (turned > maxTurn) break

    dx = dx * 0.72 + ndx * 0.28
    dy = dy * 0.72 + ndy * 0.28
    const dlen = Math.hypot(dx, dy) || 1
    dx /= dlen
    dy /= dlen

    x = cx
    y = cy
    traveled += step

    if (x < 1 || y < 1 || x > map.w - 2 || y > map.h - 2) break
    raw.push({ x: x / map.w, y: y / map.h })
  }

  if (raw.length < 4) return []
  return simplify(raw, 0.0016)
}

/** Ramer-Douglas-Peucker, so the traced run stays draggable. */
export function simplify(points: Point[], tolerance: number): Point[] {
  if (points.length < 3) return points
  const keep = new Array(points.length).fill(false)
  keep[0] = true
  keep[points.length - 1] = true

  const stack: [number, number][] = [[0, points.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()!
    let maxDist = 0
    let index = -1
    const a = points[first]
    const b = points[last]
    const vx = b.x - a.x
    const vy = b.y - a.y
    const len2 = vx * vx + vy * vy

    for (let i = first + 1; i < last; i++) {
      const p = points[i]
      let t = len2 === 0 ? 0 : ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2
      t = clamp(t, 0, 1)
      const d = Math.hypot(a.x + vx * t - p.x, a.y + vy * t - p.y)
      if (d > maxDist) {
        maxDist = d
        index = i
      }
    }
    if (maxDist > tolerance && index > 0) {
      keep[index] = true
      stack.push([first, index], [index, last])
    }
  }
  return points.filter((_, i) => keep[i])
}
